// React component

import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet";
import { FEATURES } from "@/constants/features";
import {
  ChevronRight,
  Home,
  Menu,
  Package,
  X,
  BookOpen,
  HelpCircle,
  FileText,
  Folder,
  Info,
  Mail,
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import useFeatureFlag from "@/hooks/useFeatureFlag";
import { motion } from "framer-motion";

const MobileMenu = () => {
  const location = useLocation();
  const pathname = location.pathname;
  const isRealtimePage = pathname?.includes("/features/real-time");

  // Add feature flag hooks for each feature
  const isGlobalCdnEnabled = useFeatureFlag("globalCdn");
  const isPerformanceEnabled = useFeatureFlag("performance");
  const isSecurityEnabled = useFeatureFlag("security");
  const isAiSolutionsEnabled = useFeatureFlag("aiSolutions");
  const isDevelopmentEnabled = useFeatureFlag("development");
  const isAnalyticsEnabled = useFeatureFlag("analytics");
  const isAccessControlEnabled = useFeatureFlag("accessControl");
  const isRealtimeEnabled = useFeatureFlag("realtime");

  // Add pricing page feature flag
  const isPricingPageEnabled = useFeatureFlag("pricingPage");

  // Filter features based on feature flags
  const filteredFeatures = FEATURES.filter((feature) => {
    switch (feature.title) {
      case "Global CDN":
        return isGlobalCdnEnabled;
      case "Performance":
        return isPerformanceEnabled;
      case "Security":
        return isSecurityEnabled;
      case "AI Solutions":
        return isAiSolutionsEnabled;
      case "Development":
        return isDevelopmentEnabled;
      case "Analytics":
        return isAnalyticsEnabled;
      case "Access Control":
        return isAccessControlEnabled;
      case "Real-time":
        return isRealtimeEnabled;
      default:
        return true;
    }
  });

  return (
    <Sheet data-oid="za.mx0z">
      <SheetTrigger asChild className="lg:hidden" data-oid="ie8x3j9">
        <Button
          variant="ghost"
          size="icon"
          aria-label="Open menu"
          className={`lg:hidden pointer-events-auto z-50 ${isRealtimePage ? "text-foreground hover:bg-pink-500/10" : ""}`}
          data-oid="8cxkyj5"
        >
          <Menu className="h-5 w-5 sm:h-6 sm:w-6" data-oid="_4i.::a" />
        </Button>
      </SheetTrigger>
      <SheetContent
        side="left"
        className={`w-[85vw] sm:max-w-[320px] p-0 overflow-hidden pointer-events-auto z-50 ${
          isRealtimePage
            ? "bg-background/90 backdrop-blur-xl border-r border-pink-500/10"
            : "bg-background/95"
        }`}
        data-oid="5dhz7z3"
      >
        {isRealtimePage && (
          <div
            className="absolute inset-0 z-0 overflow-hidden"
            data-oid="jlg0.ev"
          >
            <div
              className="absolute top-1/4 right-0 w-[150px] h-[150px] bg-pink-500/5 rounded-full blur-3xl"
              data-oid="5thd8lg"
            ></div>
            <div
              className="absolute bottom-1/4 left-0 w-[100px] h-[100px] bg-blue-500/5 rounded-full blur-3xl"
              data-oid="r9-2g67"
            ></div>
            <div
              className="absolute bottom-0 w-full h-[1px] bg-gradient-to-r from-transparent via-pink-500/20 to-transparent"
              data-oid="iycu0:q"
            ></div>
          </div>
        )}

        <div className="flex flex-col h-full" data-oid="5v_919_">
          {/* Header with logo and close button */}
          <div
            className={`flex items-center justify-between border-b px-4 py-3 ${
              isRealtimePage ? "border-pink-500/10" : "border-border"
            }`}
            data-oid="8.25z6t"
          >
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.2 }}
              className="flex items-center gap-2"
              data-oid="x-1r_ku"
            >
              <Menu
                className={`h-4 w-4 ${isRealtimePage ? "text-pink-500" : "text-muted-foreground"}`}
                data-oid="1aq.fg4"
              />
              <span
                className={`text-sm font-medium ${isRealtimePage ? "text-foreground" : ""}`}
                data-oid="6cwl-96"
              >
                {isRealtimePage ? "Real-time Navigation" : "Site Navigation"}
              </span>
            </motion.div>
            <SheetClose
              className={`rounded-full p-2 ${
                isRealtimePage ? "hover:bg-pink-500/10" : "hover:bg-muted"
              }`}
              data-oid="6swj3kb"
            >
              <X className="h-5 w-5" data-oid="c:p.0yb" />
              <span className="sr-only" data-oid=":huzrm4">
                Close
              </span>
            </SheetClose>
          </div>

          {/* Navigation content */}
          <div
            className="flex-1 overflow-y-auto sidebar-scroll"
            data-oid="11bse_."
          >
            <div className="px-3 py-4" data-oid="3_ke14r">
              <nav className="flex flex-col space-y-1" data-oid=".uu:k:t">
                <SheetClose asChild data-oid="-3xhulw">
                  <Link
                    to="/"
                    className={`flex items-center gap-3 py-2.5 px-3 text-sm font-medium rounded-md transition-colors relative ${
                      pathname === "/"
                        ? "bg-primary/10 text-primary"
                        : "text-foreground hover:bg-muted/80"
                    }`}
                    data-active={pathname === "/" ? "true" : "false"}
                    data-oid="dk9jvwq"
                  >
                    <Home className="h-4 w-4" data-oid="f.a1:n7" />
                    <span data-oid="55k87gs">Home</span>
                  </Link>
                </SheetClose>

                {/* Features section */}
                <div className="pt-4 pb-2" data-oid="4mruixz">
                  <div
                    className="px-3 mb-2 text-xs uppercase tracking-wider text-muted-foreground font-medium"
                    data-oid="t1830s0"
                  >
                    Features
                  </div>
                  <div className="space-y-1" data-oid="4yxqpw6">
                    <SheetClose asChild data-oid="y9y4pto">
                      <Link
                        to="/features"
                        className={`flex items-center gap-3 py-2.5 px-3 text-sm font-medium rounded-md transition-colors relative ${
                          pathname === "/features"
                            ? "bg-primary/10 text-primary"
                            : "text-foreground hover:bg-muted/80"
                        }`}
                        data-active={
                          pathname === "/features" ? "true" : "false"
                        }
                        data-oid="sr8fydg"
                      >
                        <Package className="h-4 w-4" data-oid="k9g70ej" />
                        <span data-oid="lixmhui">All Features</span>
                      </Link>
                    </SheetClose>
                    {filteredFeatures.map((feature) => {
                      const Icon = feature.icon;
                      const isActive = pathname === `/features/${feature.slug}`;
                      return (
                        <SheetClose
                          asChild
                          key={feature.slug}
                          data-oid="98lmeji"
                        >
                          <Link
                            to={`/features/${feature.slug}`}
                            className={`flex items-center justify-between py-2 px-3 text-sm rounded-md transition-colors relative ${
                              isActive
                                ? "bg-primary/10 text-primary font-medium"
                                : "text-foreground hover:bg-muted/80"
                            }`}
                            data-active={isActive ? "true" : "false"}
                            data-oid="gd-gg_v"
                          >
                            <div
                              className="flex items-center gap-3"
                              data-oid="n_l0fg6"
                            >
                              <Icon
                                className={`h-4 w-4 ${isActive ? "text-primary" : feature.color}`}
                                data-oid="mvtqqc4"
                              />
                              <span data-oid="p.6fu29">{feature.title}</span>
                            </div>
                            <ChevronRight
                              className="h-4 w-4 opacity-50"
                              data-oid="uep3tpf"
                            />
                          </Link>
                        </SheetClose>
                      );
                    })}
                  </div>
                </div>

                {/* Pages section */}
                <div className="pt-4 pb-2" data-oid="w3y-kj4">
                  <div
                    className="px-3 mb-2 text-xs uppercase tracking-wider text-muted-foreground font-medium"
                    data-oid="rjr_2h1"
                  >
                    Pages
                  </div>
                  <div className="space-y-1" data-oid="5t_om64">
                    {isPricingPageEnabled && (
                      <SheetClose asChild data-oid="9a37ebj">
                        <Link
                          to="/pricing"
                          className={`flex items-center gap-3 py-2.5 px-3 text-sm font-medium rounded-md transition-colors relative ${
                            pathname === "/pricing"
                              ? "bg-primary/10 text-primary"
                              : "text-foreground hover:bg-muted/80"
                          }`}
                          data-active={
                            pathname === "/pricing" ? "true" : "false"
                          }
                          data-oid="x4dka.9"
                        >
                          <Package className="h-4 w-4" data-oid="8t0s2ex" />
                          <span data-oid="k2-68ek">Pricing</span>
                        </Link>
                      </SheetClose>
                    )}

                    <SheetClose asChild data-oid="hf.:v7t">
                      <Link
                        to="/blog"
                        className={`flex items-center gap-3 py-2.5 px-3 text-sm font-medium rounded-md transition-colors relative ${
                          pathname === "/blog"
                            ? "bg-primary/10 text-primary"
                            : "text-foreground hover:bg-muted/80"
                        }`}
                        data-active={pathname === "/blog" ? "true" : "false"}
                        data-oid="mdzm4wh"
                      >
                        <BookOpen className="h-4 w-4" data-oid="68-lktg" />
                        <span data-oid="5t98:.o">Blog</span>
                      </Link>
                    </SheetClose>

                    {/**
                     * <SheetClose asChild data-oid="c.jwvdo">
                     *   <Link
                     *     to="/casestudy"
                     *     className={`flex items-center gap-3 py-2.5 px-3 text-sm font-medium rounded-md transition-colors relative ${
                     *       pathname === "/casestudy"
                     *         ? "bg-primary/10 text-primary"
                     *         : "text-foreground hover:bg-muted/80"
                     *     }`}
                     *     data-active={
                     *       pathname === "/casestudy" ? "true" : "false"
                     *     }
                     *     data-oid="pfloue3"
                     *   >
                     *     <FileText className="h-4 w-4" data-oid="nzvl5-w" />
                     *     <span data-oid="cx1w0j7">Case Studies</span>
                     *   </Link>
                     * </SheetClose>
                     *
                     * <SheetClose asChild data-oid=".c-zft0">
                     *   <Link
                     *     to="/resources"
                     *     className={`flex items-center gap-3 py-2.5 px-3 text-sm font-medium rounded-md transition-colors relative ${
                     *       pathname === "/resources"
                     *         ? "bg-primary/10 text-primary"
                     *         : "text-foreground hover:bg-muted/80"
                     *     }`}
                     *     data-active={
                     *       pathname === "/resources" ? "true" : "false"
                     *     }
                     *     data-oid="9y9gqx8"
                     *   >
                     *     <Folder className="h-4 w-4" data-oid="hk2igpu" />
                     *     <span data-oid="w7oktz9">Resources</span>
                     *   </Link>
                     * </SheetClose>
                     */}

                    <SheetClose asChild data-oid="s:1oaqg">
                      <Link
                        to="/faq"
                        className={`flex items-center gap-3 py-2.5 px-3 text-sm font-medium rounded-md transition-colors relative ${
                          pathname === "/faq"
                            ? "bg-primary/10 text-primary"
                            : "text-foreground hover:bg-muted/80"
                        }`}
                        data-active={pathname === "/faq" ? "true" : "false"}
                        data-oid="7h4gvni"
                      >
                        <HelpCircle className="h-4 w-4" data-oid="321_z8." />
                        <span data-oid="itr8:hw">FAQ</span>
                      </Link>
                    </SheetClose>

                    <SheetClose asChild data-oid="0pj5pl6">
                      <Link
                        to="/about"
                        className={`flex items-center gap-3 py-2.5 px-3 text-sm font-medium rounded-md transition-colors relative ${
                          pathname === "/about"
                            ? "bg-primary/10 text-primary"
                            : "text-foreground hover:bg-muted/80"
                        }`}
                        data-active={pathname === "/about" ? "true" : "false"}
                        data-oid="ol:5ep:"
                      >
                        <Info className="h-4 w-4" data-oid="vp6lg-." />
                        <span data-oid="sesu77n">About</span>
                      </Link>
                    </SheetClose>

                    <SheetClose asChild data-oid="jncs4mq">
                      <Link
                        to="/contact"
                        className={`flex items-center gap-3 py-2.5 px-3 text-sm font-medium rounded-md transition-colors relative ${
                          pathname === "/contact"
                            ? "bg-primary/10 text-primary"
                            : "text-foreground hover:bg-muted/80"
                        }`}
                        data-active={pathname === "/contact" ? "true" : "false"}
                        data-oid="15dc3-8"
                      >
                        <Mail className="h-4 w-4" data-oid="b9dtb.m" />
                        <span data-oid="5fyqo6g">Contact</span>
                      </Link>
                    </SheetClose>
                  </div>
                </div>
              </nav>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default MobileMenu;
